/**
 * 정렬·필터를 행 배열에 적용. 원본은 변경하지 않음.
 */

import type { Column, ColState } from './types';

function rawString<T>(col: Column<T>, row: T): string {
  const v = col.accessor(row);
  return v === null || v === undefined ? '' : String(v);
}

export function applyColState<T>(
  rows: T[],
  columns: Column<T>[],
  state: ColState,
): T[] {
  let result = rows;

  for (const [key, allowed] of Object.entries(state.filters)) {
    if (!allowed || allowed.size === 0) continue;
    const col = columns.find(c => c.key === key);
    if (!col) continue;
    result = result.filter(r => allowed.has(rawString(col, r)));
  }

  // 다단계 정렬 — sortStack 의 마지막 spec 부터 거꾸로 정렬 (V8 stable sort 기반).
  //   sortStack=[A,B,C] 이면 C→B→A 순으로 정렬 적용. 결과적으로 A 가 1차 키.
  //   예: 사용자가 제안가 → 거래처 순으로 클릭 → stack=[거래처, 제안가]
  //       정렬 적용: 제안가 → 거래처 → 거래처별로 묶이고 그 안에서 제안가 순.
  const stack = state.sortStack && state.sortStack.length > 0
    ? state.sortStack
    : (state.sortKey && state.sortDir
        ? [{ key: state.sortKey, dir: state.sortDir }]
        : []);

  if (stack.length > 0) {
    result = [...result];
    for (let i = stack.length - 1; i >= 0; i--) {
      const spec = stack[i]!;
      const col = columns.find(c => c.key === spec.key);
      if (!col) continue;
      const dir = spec.dir === 'desc' ? -1 : 1;
      const sortFn = col.sortAccessor ?? col.accessor;
      result.sort((a, b) => {
        const va = sortFn(a);
        const vb = sortFn(b);
        if (typeof va === 'number' || typeof vb === 'number' || col.type === 'number') {
          return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
        }
        return String(va ?? '').localeCompare(String(vb ?? ''), 'ko') * dir;
      });
    }
  }

  return result;
}

/**
 * **다른 컬럼 필터만** 적용한 행 — 필터 팝오버의 목록을 만들 때 쓴다 (엑셀식 연쇄 필터).
 *
 * 왜 자기 자신은 빼는가 — 「거래처 = A」 로 걸러 놓고 다시 그 컬럼 필터를 열었을 때
 * A 만 보이면 B 를 **되살릴 방법이 없다.** 엑셀도 자기 컬럼 필터는 목록에 반영하지 않는다.
 *
 * 정렬은 하지 않는다 (고유값만 뽑을 것이므로 불필요한 비용).
 */
export function rowsForFilterOptions<T>(
  rows: T[],
  columns: Column<T>[],
  state: ColState,
  exceptKey: string,
): T[] {
  let result = rows;
  for (const [key, allowed] of Object.entries(state.filters)) {
    if (key === exceptKey) continue;
    if (!allowed || allowed.size === 0) continue;
    const col = columns.find((c) => c.key === key);
    if (!col) continue;
    result = result.filter((r) => allowed.has(rawString(col, r)));
  }
  return result;
}

/** 컬럼의 고유 raw 값 목록 (필터 팝오버용) */
export function uniqueValues<T>(rows: T[], col: Column<T>): string[] {
  const set = new Set<string>();
  for (const r of rows) set.add(rawString(col, r));
  const arr = Array.from(set);
  if (col.type === 'number') {
    return arr.sort((a, b) => (Number(a) || 0) - (Number(b) || 0));
  }
  return arr.sort((a, b) => a.localeCompare(b, 'ko'));
}
